import { Helmet } from 'react-helmet-async';
import React from 'react';

interface Organization {
  name: string;
  url: string;
  logo: string;
  sameAs?: string[];
}

const defaultOrganization: Organization = {
  name: "recruteurs.io",
  url: "https://recruteurs.io",
  logo: "https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png",
  sameAs: [
    "https://twitter.com/recruteurs_io",
    "https://www.linkedin.com/company/recruteurs-io",
    "https://www.facebook.com/recruteurs.io"
  ]
};

export function OrganizationSchema({ organization = defaultOrganization }: { organization?: Organization }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: organization.name,
    url: organization.url,
    logo: organization.logo,
    sameAs: organization.sameAs
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface WebPage {
  title: string;
  description: string;
  url: string;
  lastReviewed?: string;
  speakable?: {
    cssSelector: string;
  };
}

export function WebPageSchema({ page }: { page: WebPage }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url: page.url,
    ...(page.lastReviewed && { datePublished: page.lastReviewed }),
    ...(page.speakable && {
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: page.speakable.cssSelector
      }
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@id": item.url,
        name: item.name
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface Article {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: {
    name: string;
    url?: string;
  };
  publisher: Organization;
  url: string;
  mainEntityOfPage: string;
}

export function ArticleSchema({ article }: { article: Article }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.headline,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      "@type": "Person",
      name: article.author.name,
      ...(article.author.url && { url: article.author.url })
    },
    publisher: {
      "@type": "Organization",
      name: article.publisher.name,
      logo: {
        "@type": "ImageObject",
        url: article.publisher.logo
      }
    },
    url: article.url,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.mainEntityOfPage
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface FAQPage {
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

export function FAQPageSchema({ questions }: FAQPage) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map(q => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}